import React from "react";
import { CheckCircle2, Zap, Rocket, ShieldCheck, ArrowRight } from "lucide-react";
import { useCMS, useSectionEnabled } from "../context/CMSContext";

export default function Hero() {
  const enabled = useSectionEnabled("hero");
  const { cms } = useCMS();
  if (!enabled) return null;
  const hero = cms?.hero || {};
  const stats = cms?.stats || [];

  return (
    <section className="lm-hero-bg relative overflow-hidden">
      <div className="absolute inset-0 lm-hero-grid opacity-70 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 pb-16 lg:pt-20 lg:pb-28 relative">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-6 items-center">
          <div>
            <h1 className="lm-font font-extrabold text-[#11151C] tracking-tight leading-[1.05] text-[34px] sm:text-5xl lg:text-[60px]">
              {hero.line1}
              <br />
              {hero.line2}
              <br />
              {hero.line3} <span className="lm-underline-swoosh">{hero.brandHighlight}</span>
            </h1>

            <p className="mt-5 sm:mt-6 text-[#11151C]/70 text-base sm:text-lg max-w-xl leading-relaxed">
              {hero.description}
            </p>

            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5 max-w-lg">
              {(hero.bullets || []).map((b) => (
                <li key={b} className="flex items-center gap-2 text-[#11151C] font-medium">
                  <CheckCircle2 className="w-5 h-5 text-[#FF6B3D] flex-shrink-0" />
                  <span className="text-sm sm:text-base">{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-4">
              <a
                href={hero.ctaUrl || "#pricing"}
                className="lm-btn-primary px-5 sm:px-6 py-3.5 rounded-xl text-sm font-bold inline-flex items-center justify-center gap-2"
              >
                <Rocket className="w-4 h-4 text-[#FF6B3D]" /> {hero.ctaText}
                <ArrowRight className="w-4 h-4" />
              </a>
              {hero.guaranteeText && (
                <span className="text-[#11151C]/70 text-sm inline-flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#FF6B3D]" />
                  {hero.guaranteeText}
                </span>
              )}
            </div>

            <div className="mt-9 flex flex-wrap items-center gap-6 sm:gap-8">
              {stats.map((s) => (
                <div key={s.label}>
                  <div className="lm-font font-extrabold text-[#11151C] text-2xl">{s.value}</div>
                  <div className="text-[#11151C]/60 text-xs uppercase tracking-wider mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative h-[360px] sm:h-[480px] lg:h-[520px] mt-4 lg:mt-0">
            <div className="absolute inset-0 flex items-center justify-center">
              {cms?.heroImage?.imageUrl && !cms?.heroImage?.useDefault ? (
                <img
                  src={cms.heroImage.imageUrl}
                  alt="Hero"
                  className="w-full max-w-md aspect-[4/5] object-cover rounded-2xl lm-float-card"
                />
              ) : (
              <div className="w-full max-w-md aspect-[4/5] lm-float-card p-6 relative">
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-red-400" />
                  <span className="w-3 h-3 rounded-full bg-yellow-400" />
                  <span className="w-3 h-3 rounded-full bg-green-400" />
                </div>
                <div className="mt-5 space-y-3">
                  <div className="h-3 w-3/4 bg-[#11151C]/10 rounded-full" />
                  <div className="h-3 w-1/2 bg-[#11151C]/10 rounded-full" />
                  <div className="h-3 w-2/3 bg-[#11151C]/10 rounded-full" />
                </div>
                <div className="mt-6 grid grid-cols-3 gap-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="aspect-square rounded-lg bg-gradient-to-br from-[#FFE6D6] to-[#FFD0B8]" />
                  ))}
                </div>
                <div className="mt-6 h-24 rounded-xl bg-gradient-to-br from-[#FFF1E5] to-[#FFE6D6] relative overflow-hidden">
                  <svg viewBox="0 0 200 60" className="absolute inset-x-0 bottom-0 w-full h-16">
                    <path d="M0 50 L 30 30 L 60 40 L 100 15 L 140 25 L 180 10 L 200 20" stroke="#FF6B3D" strokeWidth="3" fill="none" />
                  </svg>
                </div>
              </div>
              )}
            </div>

            <div className="absolute top-6 right-2 lg:right-6 lm-float-card px-3 py-2 sm:px-4 sm:py-3 flex items-center gap-3">
              <span className="w-8 h-8 sm:w-9 sm:h-9 lm-icon-tile inline-flex items-center justify-center">
                <Zap className="w-4 h-4 sm:w-5 sm:h-5" />
              </span>
              <div>
                <div className="font-bold text-[#11151C] text-xs sm:text-sm">Blazing Fast</div>
                <div className="text-[#11151C]/60 text-[10px] sm:text-xs">SSD Storage</div>
              </div>
            </div>

            <div className="absolute bottom-4 left-0 lg:left-4 lm-float-card px-3 py-2 sm:px-4 sm:py-3 flex items-center gap-3">
              <span className="w-8 h-8 sm:w-9 sm:h-9 lm-icon-tile inline-flex items-center justify-center">
                <ShieldCheck className="w-4 h-4 sm:w-5 sm:h-5" />
              </span>
              <div>
                <div className="font-bold text-[#11151C] text-xs sm:text-sm">99.9% Uptime</div>
                <div className="text-[#11151C]/60 text-[10px] sm:text-xs">Guaranteed</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
